import { Link, useParams, useNavigate } from 'react-router-dom'
import { useCategory, useDeleteCategory } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { I18nTranslationsCard } from '../components/I18nFieldsSection'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function CategoryDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: category, isLoading, error } = useCategory(id!)
  const deleteMutation = useDeleteCategory()

  const handleDelete = async () => {
    if (!confirm('이 카테고리를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return

    try {
      await deleteMutation.mutateAsync(id!)
      toast.success('카테고리가 삭제되었습니다')
      navigate('/admin/categories')
    } catch (error: unknown) {
      toast.error(`삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!category) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/categories">카테고리</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">{category.name}</h1>
          {category.description && (
            <p className="text-caption mt-2 leading-relaxed">{category.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/categories/${id}/edit`}>
            <Button>
              <Pencil className="w-4 h-4 mr-2" />
              수정
            </Button>
          </Link>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            삭제
          </Button>
        </div>
      </div>

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">ID</label>
            <div className="mt-1 text-gray-900 font-mono">{category.id}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">정렬 순서</label>
            <div className="mt-1 text-gray-900">{category.listOrder}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">상태</label>
            <div className="mt-1">
              <Badge variant={category.enabled ? 'success' : 'secondary'}>
                {category.enabled ? '활성' : '비활성'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Translations */}
      <I18nTranslationsCard nameI18n={category.nameI18n} descriptionI18n={category.descriptionI18n} />
    </div>
  )
}
